import Dinero, { Currency } from 'dinero.js';
import { A1_COMMUNITY_COLOR_PRIMARY, A1_PRO_COLOR_PRIMARY } from '../config';

export const imageFormatter = (pathOrHash?: string | null) => {
    if (pathOrHash === 'default-community.jpg' || pathOrHash === undefined || pathOrHash == null) {
        return '/images/default-community.jpg';
    }

    if (pathOrHash.startsWith('http')) {
        return pathOrHash;
    }

    if (pathOrHash.includes('-')) {
        return `https://media.admitone.com/extras/${pathOrHash}?w=800&h=800&fit=cover`;
    }

    return `https://media.admitone.com/legacy/${pathOrHash}`;
};

export const imageFormatterWide = (pathOrHash?: string | null) => {
    if (pathOrHash === 'default-community.jpg' || pathOrHash === undefined || pathOrHash == null) {
        return '/images/default-community.jpg';
    }

    if (pathOrHash.startsWith('http')) {
        return pathOrHash;
    }

    if (pathOrHash.includes('-')) {
        return `https://media.admitone.com/extras/${pathOrHash}?w=323&h=214&fit=cover`;
    }

    return `https://media.admitone.com/legacy/${pathOrHash}`;
};

export const dateFormatter = (startDate: Date, endDate: Date, timezone?: string) => {
    const timeDifference = Math.abs(endDate.getTime() - startDate.getTime());

    const hoursDifference = timeDifference / (1000 * 60 * 60);

    const start = startDate.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: timezone,
    });

    const end = endDate.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: timezone,
    });

    if (hoursDifference >= 24) {
        return `${start} - ${end}`;
    } else {
        return start;
    }
};

export const timeFormatter = (startDate: Date, endDate: Date, timezone?: string) => {
    const start = startDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: timezone,
    });

    const end = endDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: timezone,
        timeZoneName: 'short',
    });

    return `${start} - ${end}`;
};

export const SaleEndDateFormatter = (saleDate: Date, timezone: string) => {
    const endDate = saleDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZone: timezone,
        timeZoneName: 'short',
    });

    return `Sale ends on ${endDate}`;
};

export const formatTag = (tag: string | null | undefined) => {
    return tag
        ?.replace(/([A-Z])/g, ' $1') // insert a space before all capital letters
        .replace(/^./, (str) => str.toUpperCase()); // capitalize the first letter
};

export const formatPrice = (price: number, currency = 'CAD') => {
    return Dinero({ amount: price, currency: currency as Currency })
        .setLocale(currency === 'USD' ? 'en-US' : 'en-CA')
        .toFormat();
};

export const formatPrimaryColor = (
    eventType: string | null | undefined,
    organizerType: string | undefined | null,
    organizerColor: string
) => {
    switch (true) {
        case eventType === 'pro':
            return A1_PRO_COLOR_PRIMARY;
        case organizerType === 'poweredby':
            return organizerColor;
        default:
            return A1_COMMUNITY_COLOR_PRIMARY;
    }
};

export const createUrlTitle = (title: string): string => {
    const result = title
        .trim()
        // First, convert common Unicode bold characters to regular ASCII
        .replace(/𝗔/g, 'A')
        .replace(/𝗕/g, 'B')
        .replace(/𝗖/g, 'C')
        .replace(/𝗗/g, 'D')
        .replace(/𝗘/g, 'E')
        .replace(/𝗙/g, 'F')
        .replace(/𝗚/g, 'G')
        .replace(/𝗛/g, 'H')
        .replace(/𝗜/g, 'I')
        .replace(/𝗝/g, 'J')
        .replace(/𝗞/g, 'K')
        .replace(/𝗟/g, 'L')
        .replace(/𝗠/g, 'M')
        .replace(/𝗡/g, 'N')
        .replace(/𝗢/g, 'O')
        .replace(/𝗣/g, 'P')
        .replace(/𝗤/g, 'Q')
        .replace(/𝗥/g, 'R')
        .replace(/𝗦/g, 'S')
        .replace(/𝗧/g, 'T')
        .replace(/𝗨/g, 'U')
        .replace(/𝗩/g, 'V')
        .replace(/𝗪/g, 'W')
        .replace(/𝗫/g, 'X')
        .replace(/𝗬/g, 'Y')
        .replace(/𝗭/g, 'Z')
        .replace(/𝗮/g, 'a')
        .replace(/𝗯/g, 'b')
        .replace(/𝗰/g, 'c')
        .replace(/𝗱/g, 'd')
        .replace(/𝗲/g, 'e')
        .replace(/𝗳/g, 'f')
        .replace(/𝗴/g, 'g')
        .replace(/𝗵/g, 'h')
        .replace(/𝗶/g, 'i')
        .replace(/𝗷/g, 'j')
        .replace(/𝗸/g, 'k')
        .replace(/𝗹/g, 'l')
        .replace(/𝗺/g, 'm')
        .replace(/𝗻/g, 'n')
        .replace(/𝗼/g, 'o')
        .replace(/𝗽/g, 'p')
        .replace(/𝗾/g, 'q')
        .replace(/𝗿/g, 'r')
        .replace(/𝘀/g, 's')
        .replace(/𝘁/g, 't')
        .replace(/𝘂/g, 'u')
        .replace(/𝘃/g, 'v')
        .replace(/𝘄/g, 'w')
        .replace(/𝘅/g, 'x')
        .replace(/𝘆/g, 'y')
        .replace(/𝘇/g, 'z')
        // Remove emojis and special characters, keep only letters, numbers, and spaces
        .replace(/[^\x00-\x7F\s]/g, '') // Remove all non-ASCII except spaces
        .replace(/[^a-zA-Z0-9\s]/g, '') // Keep only alphanumeric and spaces
        .toLowerCase()
        .replace(/\s+/g, '-') // Replace spaces with dashes
        .replace(/^-+|-+$/g, ''); // Remove leading/trailing dashes

    return result;
};

export const createSlug = (text: string): string => {
    return text
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
};
